import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Agent } from '../../types/agent-types-new';
import { 
  Activity, 
  Brain, 
  Clock, 
  Target,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';

interface AgentProfileDrawerProps {
  agent: Agent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AgentProfileDrawer({ agent, open, onOpenChange }: AgentProfileDrawerProps) {
  if (!agent) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const stats = agent.stats || {
    totalExecutions: 0,
    successRate: 0,
    averageExecutionTime: 0,
    lastExecutionTime: null
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5" />
              <SheetTitle>{agent.name}</SheetTitle>
            </div>
            <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
              {agent.status}
            </Badge>
          </div>
          <SheetDescription>{agent.description}</SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="configuration">Configuration</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Agent Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">ID</h4>
                  <p className="text-sm text-muted-foreground font-mono">{agent.id}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Type</h4>
                  <Badge variant="outline">{agent.type}</Badge>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Category</h4>
                  <p className="text-sm text-muted-foreground">{agent.category || 'Uncategorized'}</p>
                </div>
                {agent.version && (
                  <div>
                    <h4 className="text-sm font-medium mb-1">Version</h4>
                    <p className="text-sm text-muted-foreground">{agent.version}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{stats.totalExecutions}</p>
                    <p className="text-xs text-muted-foreground">Total Executions</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{(stats.successRate || 0).toFixed(1)}%</p>
                    <p className="text-xs text-muted-foreground">Success Rate</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{(stats.averageExecutionTime || 0).toFixed(2)}s</p>
                    <p className="text-xs text-muted-foreground">Avg Execution Time</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {stats.lastExecutionTime 
                        ? format(new Date(stats.lastExecutionTime), 'MMM d, HH:mm')
                        : 'Never'}
                    </p>
                    <p className="text-xs text-muted-foreground">Last Execution</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="configuration" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Model Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Model</h4>
                  <p className="text-sm text-muted-foreground">
                    {agent.settings?.model || 'gpt-4-turbo-preview'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Temperature</h4>
                  <p className="text-sm text-muted-foreground">
                    {agent.settings?.temperature || 0.7}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Max Tokens</h4>
                  <p className="text-sm text-muted-foreground">
                    {agent.settings?.max_tokens || 1000}
                  </p>
                </div>
              </CardContent>
            </Card>

            {agent.settings?.systemPrompt && (
              <Card>
                <CardHeader>
                  <CardTitle>System Prompt</CardTitle>
                </CardHeader>
                <CardContent>
                  <pre className="text-sm whitespace-pre-wrap bg-muted p-3 rounded">
                    {agent.settings.systemPrompt}
                  </pre>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="performance" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
                <CardDescription>
                  Agent performance over the last 30 days
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Success Rate</span>
                    <span className="text-sm text-muted-foreground">
                      {(stats.successRate || 0).toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={stats.successRate || 0} className="h-2" />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Reliability Score</span>
                    <span className="text-sm text-muted-foreground">
                      {((stats.successRate || 0) * 0.01 * 100).toFixed(0)}%
                    </span>
                  </div>
                  <Progress value={(stats.successRate || 0) * 0.01 * 100} className="h-2" />
                </div>

                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Execution History</h4>
                  <div className="text-sm text-muted-foreground">
                    <p>Total Executions: {stats.totalExecutions}</p>
                    <p>Failed Executions: {Math.round(stats.totalExecutions * (1 - (stats.successRate || 0) / 100))}</p>
                    <p>Average Response Time: {(stats.averageExecutionTime || 0).toFixed(2)}s</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-6 flex gap-2">
          <Button className="flex-1">
            Configure Agent
          </Button>
          <Button variant="outline" className="flex-1">
            View Logs
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}